import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../lib/auth";
import { api } from "../lib/api";
import { LinkedInConnectionCard } from "../components/LinkedInConnectionCard";
import { useLinkedInStatus } from "../hooks/useLinkedInStatus";
import {
  useSystemConfig,
  useToggleScraper,
  useToggleOperationalAlerts,
} from "../hooks/useSystemConfig";
import { useScrapeNow } from "../hooks/useScrapeNow";
import { usePasskeys, useRegisterPasskey, useRemovePasskey } from "../hooks/usePasskey";
import { Button } from "../components/ui/Button";
import { Switch } from "../components/ui/Switch";
import { toast } from "../lib/toast";
import type { ScrapeTarget } from "../lib/api";

const TARGET_LABEL: Record<ScrapeTarget, string> = {
  feed: "comentários",
  messages: "DMs",
};

const Settings = () => {
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: linkedinStatus } = useLinkedInStatus();
  const { data: cfg } = useSystemConfig();
  const toggleScraper = useToggleScraper();
  const toggleAlerts = useToggleOperationalAlerts();
  const scrapeNow = useScrapeNow();
  const passkeys = usePasskeys();
  const registerPasskey = useRegisterPasskey();
  const removePasskey = useRemovePasskey();

  const handleRegisterPasskey = () =>
    registerPasskey.mutate(undefined, {
      onSuccess: () => toast.success("Passkey registrado neste dispositivo"),
      onError: (err: Error) =>
        toast.error(
          err.name === "NotAllowedError" ? "Registro cancelado" : "Falha ao registrar passkey",
          { description: err.message }
        ),
    });

  const runScrape = (target: ScrapeTarget) =>
    scrapeNow.mutate(target, {
      onSuccess: (run) => {
        if (run.status === "failed") {
          // The scraper closes the run itself when the guard trips, so this is
          // the captcha/checkpoint path — not a transport error.
          toast.error(`Coleta de ${TARGET_LABEL[target]} falhou`, {
            description: run.error ?? "Motivo desconhecido — veja os logs.",
          });
          return;
        }
        toast.success(`Coleta de ${TARGET_LABEL[target]} concluída`, {
          description:
            run.collected > 0
              ? `${run.collected} ${run.collected === 1 ? "novo item" : "novos itens"} no Inbox.`
              : "Nada novo encontrado.",
        });
      },
      onError: (err: Error) =>
        toast.error(`Falha ao rodar coleta de ${TARGET_LABEL[target]}`, {
          description: err.message,
        }),
    });

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // ignora — cookie pode estar inválido já
    }
    qc.clear();
    toast.success("Sessão encerrada");
    navigate("/login", { replace: true });
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Configurações e estado da sessão.
        </p>
      </div>

      <SettingsGroup
        title="Conta"
        description="Sessão e identidade"
      >
        <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <dl className="text-sm space-y-1">
            <div className="flex">
              <dt className="text-zinc-500 dark:text-zinc-400 w-24">Email</dt>
              <dd className="text-zinc-900 dark:text-zinc-100">{session.email ?? "?"}</dd>
            </div>
          </dl>
          <div className="mt-4">
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Sair
            </Button>
          </div>
        </section>

        <section
          className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
          data-testid="passkey-panel"
        >
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Passkey (Face ID / Touch ID)
          </h3>
          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 mb-3">
            Registre este dispositivo pra entrar com biometria, sem depender de email.
          </p>

          {passkeys.data && passkeys.data.length > 0 && (
            <ul className="mb-3 space-y-2">
              {passkeys.data.map((pk) => (
                <li
                  key={pk.credentialId}
                  data-testid="passkey-item"
                  data-credential-id={pk.credentialId}
                  className="flex items-center justify-between text-xs text-zinc-600 dark:text-zinc-400"
                >
                  <span>
                    {pk.deviceName ?? "Dispositivo"} · registrado em{" "}
                    {new Date(pk.createdAt).toLocaleDateString("pt-BR")}
                  </span>
                  <button
                    type="button"
                    data-testid="passkey-remove"
                    onClick={() =>
                      removePasskey.mutate(pk.credentialId, {
                        onSuccess: () => toast.success("Passkey removido"),
                        onError: (err: Error) =>
                          toast.error("Falha ao remover passkey", { description: err.message }),
                      })
                    }
                    className="text-red-600 hover:underline dark:text-red-400"
                  >
                    Remover
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Button
            variant="secondary"
            size="sm"
            data-testid="passkey-register"
            loading={registerPasskey.isPending}
            onClick={handleRegisterPasskey}
          >
            Registrar este dispositivo
          </Button>
        </section>
      </SettingsGroup>

      <SettingsGroup
        title="Conteúdo"
        description="Voz e geração de posts"
      >
        <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Voice profile
          </h3>
          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 mb-3">
            Visualize e refine o voice profile que guia a geração de posts.
          </p>
          <Link
            to="/settings/voice"
            className="inline-flex items-center justify-center rounded-lg bg-accent text-accent-fg hover:bg-accent-hover px-4 h-9 text-sm font-medium transition-colors focus-ring"
          >
            Editar voice profile
          </Link>
        </section>
      </SettingsGroup>

      <SettingsGroup
        title="Integrações & Operação"
        description="Conexões externas e jobs automatizados"
      >
        <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <Switch
            checked={!!cfg?.scraperEnabled}
            onCheckedChange={(next) =>
              toggleScraper.mutate(next, {
                onSuccess: (updated) =>
                  toast.success(
                    updated.scraperEnabled ? "Scraper ativado" : "Scraper desativado"
                  ),
                onError: (err: Error) =>
                  toast.error("Falha ao alternar scraper", { description: err.message }),
              })
            }
            label="Scraper LinkedIn"
            description="Cron 3x/dia coleta comentários e DMs. Mantenha desligado até gerar cookies e validar em dev."
          />
          {cfg?.cookieHealth ? (
            <p
              className={`text-xs mt-3 ${
                cfg.cookieHealth.status === "expired"
                  ? "text-red-600 dark:text-red-400"
                  : cfg.cookieHealth.status === "warn"
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              Cookies capturados há {cfg.cookieHealth.ageDays}{" "}
              {cfg.cookieHealth.ageDays === 1 ? "dia" : "dias"} · {cfg.cookieHealth.status}
              {cfg.cookieHealth.status !== "fresh"
                ? " — rode `pnpm run cookies:generate` localmente"
                : ""}
            </p>
          ) : (
            <p className="text-xs mt-3 text-zinc-400 dark:text-zinc-500">
              Cookies ainda não capturados.
            </p>
          )}

          <div
            className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-800"
            data-testid="scrape-now-panel"
          >
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
              Rodar agora ignora a janela noturna e o kill switch — a detecção de
              captcha continua ativa. Pode levar alguns minutos.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                data-testid="scrape-now-panel-feed"
                loading={scrapeNow.runningTarget === "feed"}
                disabled={scrapeNow.isPending}
                onClick={() => runScrape("feed")}
              >
                Coletar comentários
              </Button>
              <Button
                variant="secondary"
                data-testid="scrape-now-panel-messages"
                loading={scrapeNow.runningTarget === "messages"}
                disabled={scrapeNow.isPending}
                onClick={() => runScrape("messages")}
              >
                Coletar DMs
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <Switch
            checked={!!cfg?.operationalAlertsEnabled}
            onCheckedChange={(next) =>
              toggleAlerts.mutate(next, {
                onSuccess: (updated) =>
                  toast.success(
                    updated.operationalAlertsEnabled
                      ? "Alertas por email ativados"
                      : "Alertas por email silenciados"
                  ),
                onError: (err: Error) =>
                  toast.error("Falha ao alternar alertas", { description: err.message }),
              })
            }
            label="Alertas operacionais por email"
            description="Avisos de token LinkedIn expirado, cookies vencidos e falhas do scraper. Desligado por padrão — o banner no app continua aparecendo mesmo com isto off."
          />
        </section>

        <LinkedInConnectionCard status={linkedinStatus} />
      </SettingsGroup>
    </div>
  );
};

const SettingsGroup = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-3">
    <div>
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
      {description && (
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{description}</p>
      )}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

export default Settings;
